import { useState, type ReactNode, type CSSProperties } from "react";

/**
 * Atelier-Style Collapsible-Sektion für den Inspector (Sprint UX-Inspector
 * 2026-05-06). Schmaler Header mit Chevron + Counter, klickbar zum
 * Auf-/Zuklappen. Default wird per `defaultOpen` gesteuert.
 *
 * Verwendung:
 *   <CollapsibleSection title="LV-Positionen" counter={9}>
 *     ...
 *   </CollapsibleSection>
 */
export interface CollapsibleSectionProps {
  title: string;
  /** Optionaler Icon-String (Emoji oder kurzes Symbol), wird vor dem Titel angezeigt. */
  icon?: string;
  /** Zahl in Klammern hinter dem Titel — z.B. Anzahl Türen. */
  counter?: number;
  /** Default-Zustand. */
  defaultOpen?: boolean;
  /** Optionale Buttons rechts im Header (z.B. „+ Tür"). */
  rightSlot?: ReactNode;
  /** Style-Override für den Container. */
  style?: CSSProperties;
  /** Title-Tooltip. */
  hint?: string;
  children: ReactNode;
}

export function CollapsibleSection({
  title,
  icon,
  counter,
  defaultOpen = false,
  rightSlot,
  style,
  hint,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        marginBottom: 8,
        border: "1px solid var(--bd-line)",
        borderRadius: 6,
        background: "var(--bd-surface)",
        ...style,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 10px",
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setOpen((o) => !o)}
        title={hint}
        role="button"
      >
        <span
          style={{
            fontSize: 10,
            color: "var(--bd-text-3)",
            fontFamily: "var(--bd-mono)",
            width: 10,
            textAlign: "center",
          }}
        >
          {open ? "▾" : "▸"}
        </span>
        {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: "var(--bd-text)",
            flex: 1,
          }}
        >
          {title}
          {typeof counter === "number" && (
            <span
              style={{
                marginLeft: 4,
                color: "var(--bd-text-3)",
                fontWeight: 400,
                fontFamily: "var(--bd-mono)",
                fontSize: 11,
              }}
            >
              ({counter})
            </span>
          )}
        </span>
        {rightSlot && (
          <div
            style={{ display: "flex", alignItems: "center", gap: 4 }}
            onClick={(e) => e.stopPropagation()}
          >
            {rightSlot}
          </div>
        )}
      </div>
      {open && (
        <div
          style={{
            padding: "0 10px 10px",
            borderTop: "1px solid var(--bd-line)",
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}
